import React, { useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import VoucherDisplay from "./VoucherDisplay";
import CheckedCircleIcon from "../../../components/icon-components/CheckedCircleIcon";
import TicketIcon from "../../../components/icon-components/TicketIcon";
import KeyboardArrowLeft from "../../../components/icon-components/KeyboardArrowLeft";
import KeyboardArrowDown from "../../../components/icon-components/KeyboardArrowDown";
import styles from "../styles/MilestoneCard.module.scss";
import { formatDateMonthDateYear } from "../../../utility/functions/common-functions";
import { useCompleteRedemptionMutation } from "../services/CompleteRedemptionAPISlice";
import { useSnackbar } from "notistack";
import { useAppSelector } from "../../../store/hooks";
import CancelIcon from "@mui/icons-material/Cancel";
import {
  REDEMPTION_STATUS_ID_MAPPING,
  TIER_CLASSIFICATION,
} from "../../../utility/constants/common-constants";
import FinalRewardTicket from "../../../components/icon-components/FinalRewardTicket";
import CustomButton from "../../../components/custom-button/CustomButton";

interface Milestone {
  patientProfileId: number;
  patientCaseId: number;
  tierClassificationId: number;
  tierDisplayName: string;
  tierSequence: number;
  redemptionCouponCode: string;
  statusDisplayName: string;
  totalEarnedRewardPoints: number | null;
  validTill: string | null;
  color: string;
  tierMessage?: string;
  adminEmail?: string | null;
  redeemedDate?: string | null;
  redemptionStatusId: number;
}

interface MilestoneCardProps {
  milestone: Milestone;
}

const MilestoneCard: React.FC<MilestoneCardProps> = ({ milestone }) => {
  const [expanded, setExpanded] = useState<number | null>(null);
  const [completeRedemption] = useCompleteRedemptionMutation();
  const { enqueueSnackbar } = useSnackbar();
  const accessToken = useAppSelector(
    (state) => state.adminAuth.user?.accessToken!
  );
  const email = useAppSelector((state) => state.adminAuth.user?.email!);
  const name = useAppSelector((state) => state.adminAuth.user?.name!);

  const handleCompleteRedemption = async () => {
    const body = {
      patientProfileId: milestone.patientProfileId,
      patientCaseId: milestone.patientCaseId,
      tierClassificationId: milestone.tierClassificationId,
      updatedBy: name,
      adminEmail: email,
    };
    try {
      await completeRedemption({
        body,
        accessToken,
      }).unwrap();
      enqueueSnackbar(`Voucher code redeemed successfully`, {
        variant: "success",
      });
    } catch (error: any) {
      enqueueSnackbar(
        error?.data?.message || "Failed to redeem voucher. Please try again.",
        { variant: "error" }
      );
    }
  };

  const handleExpandCollapse = (milestoneId: number) => {
    setExpanded((prev) => (prev === milestoneId ? null : milestoneId));
  };

  const getIconComponent = (statusId: number) => {
    switch (statusId) {
      case REDEMPTION_STATUS_ID_MAPPING.completed:
        return <CheckedCircleIcon variant="completed" />;
      case REDEMPTION_STATUS_ID_MAPPING.notYetEligible:
        return <CheckedCircleIcon variant="notStarted" />;
      case REDEMPTION_STATUS_ID_MAPPING.eligible:
        return <CheckedCircleIcon variant="pending" />;
      case REDEMPTION_STATUS_ID_MAPPING.pending:
        return <TicketIcon />;
      case REDEMPTION_STATUS_ID_MAPPING.expired:
        return <CancelIcon sx={{ fontSize: "21px", color: "#ff0000" }} />;
      default:
        return null;
    }
  };

  const getStatusColor = (statusId: number) => {
    switch (statusId) {
      case REDEMPTION_STATUS_ID_MAPPING.completed:
        return "#2BA948";
      case REDEMPTION_STATUS_ID_MAPPING.eligible:
        return "#FF6200";
      case REDEMPTION_STATUS_ID_MAPPING.expired:
        return "#ED4C5C";
      default:
        return "#403C3C";
    }
  };

  return (
    <Box
      className={styles.milestoneCard}
      sx={{
        border: `2px solid ${milestone.color}`,
      }}
    >
      <Box
        className={styles.dot}
        sx={{
          backgroundColor: milestone.color,
        }}
      ></Box>
      <Box className={styles.cardHeader}>
        <Typography variant="subtitle2" className={styles.milestoneTitle}>
          <IconButton
            onClick={() => handleExpandCollapse(milestone.tierSequence)}
            size="small"
            sx={{
              m: 0,
              p: 0,
              opacity: !milestone.redemptionCouponCode ? 0.5 : 1,
            }}
            disabled={!milestone.redemptionCouponCode}
          >
            {expanded ? (
              <KeyboardArrowDown />
            ) : (
              <KeyboardArrowLeft style={{ transform: "Rotate(180deg)" }} />
            )}
          </IconButton>
          {milestone.tierDisplayName}
        </Typography>
        <Typography
          variant="subtitle1"
          color={getStatusColor(milestone.redemptionStatusId)}
        >
          {milestone.statusDisplayName}
        </Typography>
        <Box>{getIconComponent(milestone.redemptionStatusId)}</Box>
      </Box>
      <Box className={styles.cardBody}>
        {milestone.redemptionCouponCode &&
          expanded === milestone.tierSequence &&
          milestone.tierClassificationId !==
            TIER_CLASSIFICATION.MOMMY_MEMBER && (
            <Box>
              {milestone.tierClassificationId !==
              TIER_CLASSIFICATION.FINAL_REWARDS ? (
                <VoucherDisplay
                  tierDisplayName={milestone.tierDisplayName}
                  redemptionCouponCode={milestone.redemptionCouponCode}
                  validTill={milestone.validTill}
                  tierSequence={milestone.tierSequence}
                  redemptionStatusId={milestone.redemptionStatusId}
                  adminEmail={milestone.adminEmail}
                  redeemedDate={formatDateMonthDateYear(
                    milestone.redeemedDate || ""
                  )}
                  handleClick={handleCompleteRedemption}
                />
              ) : (
                <FinalRewardTicket
                  leftSection={
                    <Box className={styles.leftSection}>
                      {milestone.validTill && (
                        <Typography fontSize="10px">{`valid untill ${formatDateMonthDateYear(
                          milestone.validTill
                        )}`}</Typography>
                      )}
                      {milestone.redemptionStatusId !==
                        REDEMPTION_STATUS_ID_MAPPING.completed && (
                        <CustomButton
                          text="Accept Voucher"
                          variant="outlined"
                          sx={{
                            padding: "2px 10px",
                            mt: 1,
                            mb: 2,
                            "&.Mui-disabled": {
                              opacity: 0.5,
                              border: "1px solid #a41e35",
                              color: "#a41e35",
                            },
                          }}
                          disabled={
                            milestone.redemptionStatusId ===
                            REDEMPTION_STATUS_ID_MAPPING.expired
                          }
                          onClick={handleCompleteRedemption}
                        />
                      )}
                    </Box>
                  }
                  rightSection={
                    <Box className={styles.rightSection}>
                      <Typography fontSize="10px" color="#fff" mb={1}>
                        Voucher Generated
                      </Typography>
                    </Box>
                  }
                  width="100%"
                  height="100%"
                />
              )}
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default MilestoneCard;
